<template>
  <v-dialog
      v-model="dialog"
      :width="dialogWidth"
      @keydown.enter.prevent="save"
  >
    <v-card
        class="resizable-card"
        :class="{ 'no-select': resizing }"
        :style="{ height: dialogHeight + 'px' }"
    >
      <v-card-title>
        <span class="headline">Guard Rule Config</span>
      </v-card-title>
      <v-card-text class="resizable-card__body">
        <v-row dense>
          <v-col cols="12" sm="12" md="12">
            <r-item-dropdown :model.sync="model.item" />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" sm="5" md="5">
            <v-text-field v-model.number="model.amount" label="Amount" hide-details />
          </v-col>
          <v-col cols="12" sm="1" md="1"></v-col>
          <v-col cols="12" sm="6" md="6">
            <v-checkbox v-model="model.strict" label="Strict Mode" hide-details />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" sm="6" md="6">
            <v-checkbox v-model="model.takeLocked" label="Take Locked" hide-details />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="resizable-card__actions">
        <v-spacer />
        <v-btn @click="cancel" color="blue darken-1" text>
          Cancel
        </v-btn>
        <v-btn @click="save" color="blue darken-1" text>
          Save
        </v-btn>
      </v-card-actions>
      <div class="resize-handle" @mousedown="startResize"></div>
    </v-card>
  </v-dialog>
</template>

<script>
import VueTypes from "vue-types";
import { ViewRules } from "@/common/ViewRules";
import { GuardRule } from "@/modules/redactor/models/guard/GuardRule";
import RItemDropdown from "@/modules/redactor/views/item/r-item-dropdown.vue";

export default {
  name: "r-guard-rule-dialog",
  components: { RItemDropdown },
  data: () => ({
    dialog: false,
    resolve: null,
    reject: null,
    model: VueTypes.objectOf(GuardRule),
    rules: ViewRules.READONLY,
    existingItems: [],
    originalItem: null,
    dialogWidth: 345,
    dialogHeight: 286,
    resizing: false,
    resizeStartX: 0,
    resizeStartY: 0,
    resizeStartWidth: 345,
    resizeStartHeight: 286,
    viewportPadding: 32
  }),
  methods: {
    show(rules, model, existingItems = [], originalItem = null) {
      this.dialog = true;
      this.rules = rules;
      this.model = model || new GuardRule();
      this.existingItems = existingItems || [];
      this.originalItem = originalItem;

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },

    checkDuplicate() {
      if (!this.model.item || !this.model.item.kind || this.model.item.level === undefined) {
        return false;
      }

      const newKey = this.model.item.kind.key;
      const newLevel = this.model.item.level;

      return this.existingItems.some(item => {
        if (this.originalItem && item === this.originalItem) {
          return false;
        }

        const sameKey = item.item?.kind?.key === newKey;
        const sameLevel = item.item?.level === newLevel;

        return sameKey && sameLevel;
      });
    },

    save() {
      if (!this.model.valid()) {
        this.$alert("Invalid config!");
        return;
      }

      if (this.checkDuplicate()) {
        const itemName = this.model.item?.kind?.key || "Item";
        const itemLevel = this.model.item?.level !== undefined ? this.model.item.level : "unknown";

        this.$alert(
            `Item ${itemName} (${itemLevel}) already exists in the list!`,
            "Duplicate Item"
        );
        return;
      }

      this.resolve && this.resolve(this.model);
      this.discard();
    },

    cancel() {
      this.reject && this.reject();
      this.discard();
    },

    discard() {
      this.dialog = false;
      this.existingItems = [];
      this.originalItem = null;
      this.stopResize();
    },

    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.dialogWidth;
      this.resizeStartHeight = this.dialogHeight;
      window.addEventListener("mousemove", this.onResize);
      window.addEventListener("mouseup", this.stopResize);
    },

    onResize(e) {
      if (!this.resizing) return;
      const dx = e.clientX - this.resizeStartX;
      const dy = e.clientY - this.resizeStartY;
      let newWidth = this.resizeStartWidth + dx;
      let newHeight = this.resizeStartHeight + dy;
      const minWidth = 280;
      const minHeight = 210;
      const maxWidth = window.innerWidth - this.viewportPadding;
      const maxHeight = window.innerHeight - this.viewportPadding;
      if (newWidth < minWidth) newWidth = minWidth;
      if (newWidth > maxWidth) newWidth = maxWidth;
      if (newHeight < minHeight) newHeight = minHeight;
      if (newHeight > maxHeight) newHeight = maxHeight;
      this.dialogWidth = newWidth;
      this.dialogHeight = newHeight;
    },

    stopResize() {
      if (!this.resizing) return;
      this.resizing = false;
      window.removeEventListener("mousemove", this.onResize);
      window.removeEventListener("mouseup", this.stopResize);
    }
  },
  beforeDestroy() {
    window.removeEventListener("mousemove", this.onResize);
    window.removeEventListener("mouseup", this.stopResize);
  }
};
</script>

<style scoped>
.resizable-card {
  position: relative;
  display: flex;
  flex-direction: column;
}
.resizable-card__body {
  flex: 1;
  overflow: auto;
}
.resizable-card__actions {
  flex-shrink: 0;
}
.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: se-resize;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid rgba(0, 0, 0, 0.3);
}
.no-select {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>